import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, FileText, Loader2, ChevronRight, ChevronDown,
} from 'lucide-react';
import UploadPage from './UploadPage';
import ReceiptViewerModal from '../shared/ReceiptViewerModal';
import AuthedReceiptPreview from '../../shared/components/AuthedReceiptPreview';
import { AMBER, NAVY, BORDER_COLOR, bubbleStyle, BASE_CSS } from '../../styles/tokens';
import { STATUS_COLORS, STATUS_LABEL, PAYMENT_METHODS } from '../../../../shared/constants';
import client from '../../api/client';
import DetalleNoDisponible, { motivoDeCarga } from '../shared/DetalleNoDisponible';

function fmt(amount) {
  if (amount === null || amount === undefined) return '—';
  return '$' + parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <span style={{
      background: s.bg, color: s.text, border: `1px solid ${s.border}`,
      borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: '600',
    }}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function ItemStatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <span style={{
      background: s.bg, color: s.text, border: `1px solid ${s.border}`,
      borderRadius: 20, padding: '1px 7px', fontSize: 10, fontWeight: '600',
    }}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

export default function ReceiptDetailPage({ onToast }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backTo = location.state?.from || '/employee';
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorCarga, setErrorCarga] = useState(null);  // B-1
  const [showReceiptModal, setShowReceiptModal] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const res = await client.get(`/api/receipts/${id}`);
        setReceipt(res.data);
      } catch (err) {
        console.error('Failed to load receipt:', err);
        setReceipt(null);
        setErrorCarga(motivoDeCarga(err));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id]);

  if (loading) {
    return (
      <div className="page-pad" style={{ padding: '24px 28px', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 80, color: '#94a3b8', gap: 8 }}>
          <Loader2 size={20} className="spin" /> Cargando recibo…
        </div>
      </div>
    );
  }

  if (!receipt) {
    return <DetalleNoDisponible motivo={errorCarga} onVolver={() => navigate(backTo)} volverLabel="Regresar" />;
  }

  const isEditable = receipt.status === 'draft' || receipt.status === 'sent_back' || receipt.status === 'partially_returned';

  // For editable receipts, render UploadPage in edit mode
  if (isEditable) {
    return (
      <UploadPage
        initialItems={null}
        editReceipt={receipt}
        onSubmitted={async (count) => {
          if (onToast) onToast('submitted', `Recibo enviado para revisión`);
          navigate(backTo);
        }}
        onSavedDraft={async (count) => {
          if (onToast) onToast('draft', `Recibo guardado como borrador`);
          navigate(backTo);
        }}
        onDiscarded={() => {
          if (onToast) onToast('discarded', 'Cambios descartados');
          navigate(backTo);
        }}
        onResubmitted={async () => {
          if (onToast) onToast('resubmitted', 'Recibo reenviado para revisión');
          navigate(backTo);
        }}
        onBack={() => navigate(backTo)}
      />
    );
  }

  // Read-only view for pending/approved/rejected receipts
  const items = receipt.items || [];
  const previewSrc = receipt.previewUrl;

  // Group items by project
  const itemsByProject = {};
  for (const item of items) {
    const key = item.projectName || '(Sin Proyecto)';
    if (!itemsByProject[key]) itemsByProject[key] = [];
    itemsByProject[key].push(item);
  }
  const projectGroups = Object.entries(itemsByProject);

  // Feedback is shown inline per project section

  return (
    <div className="page-pad" style={{ padding: '24px 28px', maxWidth: 1100, margin: '0 auto', animation: 'fadeUp 0.3s ease' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
        <button onClick={() => navigate(backTo)} style={{
          background: 'transparent', border: `1px solid ${BORDER_COLOR}`,
          color: '#475569', borderRadius: 8, padding: '7px 14px',
          fontWeight: '600', fontSize: 13, cursor: 'pointer',
          fontFamily: 'Inter, sans-serif', display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <ArrowLeft size={14} /> Regresar
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <h2 style={{ fontSize: 18, fontWeight: '700', color: NAVY, margin: 0 }}>
              {receipt.receiptNumber ? `#${receipt.receiptNumber}` : 'Recibo'}
              {' — '}
              {receipt.vendor || '(Sin proveedor)'}
            </h2>
            <StatusBadge status={receipt.displayStatus || receipt.status} />
            {receipt.status === 'approved' && receipt.reviewerName && (() => {
              const supervisorIds = new Set((receipt.items || items || []).map(i => i.supervisorId || i.supervisor_id));
              if (!supervisorIds.has(receipt.reviewedBy)) {
                return (
                  <span style={{
                    background: '#dbeafe', color: '#1d4ed8', border: '1px solid #93c5fd',
                    borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: '600',
                  }}>
                    Aprobado por Administrador: {receipt.reviewerName}
                  </span>
                );
              }
              return null;
            })()}
          </div>
        </div>
      </div>

      {/* Approval progress tracker */}
      {receipt.status !== 'draft' && (
        <div style={{
          ...bubbleStyle, padding: '10px 16px', marginBottom: 14,
          display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
          background: '#f8fafc',
        }}>
          <span style={{ fontSize: 12, color: '#64748b', marginRight: 4 }}>Aprobación:</span>
          {(() => {
            const st = receipt.displayStatus || receipt.status;
            const done = (n) => <span key={n} style={{ fontSize: 11, color: '#16a34a', fontWeight: 600 }}>{n} ✓</span>;
            const cur = (n) => <span key={n} style={{ fontSize: 11, fontWeight: 700, color: '#d97706', padding: '1px 6px', background: '#fef3c7', borderRadius: 6 }}>{n}</span>;
            const pend = (n) => <span key={n} style={{ fontSize: 11, color: '#94a3b8' }}>{n}</span>;
            const ret = (n) => <span key={n} style={{ fontSize: 11, color: '#ea580c', fontWeight: 600 }}>{n} ↩</span>;
            const rej = (n) => <span key={n} style={{ fontSize: 11, color: '#ef4444', fontWeight: 600 }}>{n} ✗</span>;
            const arr = (i) => <span key={`a${i}`} style={{ color: '#cbd5e1', fontSize: 11 }}> → </span>;
            const steps = [];
            // Ingeniero L1
            if (['pending_review', 'approved', 'paid'].includes(st)) steps.push(done('Ingeniero(a)'));
            else if (['pending', 'partially_returned'].includes(st)) steps.push(cur('Ingeniero(a)'));
            else if (st === 'sent_back') steps.push(ret('Ingeniero(a)'));
            else if (st === 'rejected') steps.push(rej('Ingeniero(a)'));
            else steps.push(pend('Ingeniero(a)'));
            steps.push(arr(1));
            // Rev. Final
            if (['approved', 'paid'].includes(st)) steps.push(done('Rev. Final'));
            else if (st === 'pending_review') steps.push(cur('Rev. Final'));
            else steps.push(pend('Rev. Final'));
            return steps;
          })()}
        </div>
      )}

      {/* Receipt info card */}
      <div style={{ ...bubbleStyle, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>

          {/* Receipt image */}
          <div
            onClick={() => previewSrc && setShowReceiptModal(true)}
            title={previewSrc ? 'Ver recibo en grande' : ''}
            style={{
              width: 100, height: 100, borderRadius: 10, overflow: 'hidden',
              border: `1px solid ${BORDER_COLOR}`, background: '#f1f5f9',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: previewSrc ? 'pointer' : 'default', flexShrink: 0,
            }}
          >
            {previewSrc && receipt.fileType?.startsWith('image/') ? (
              <AuthedReceiptPreview src={previewSrc} fileType={receipt.fileType} alt="" imgStyle={{ width: '100%', height: '100%', objectFit: 'cover' }} style={{ width: '100%', height: '100%' }} />
            ) : (
              <FileText size={32} style={{ color: '#94a3b8' }} />
            )}
          </div>

          {/* Receipt fields — read-only */}
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Proveedor</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: NAVY }}>{receipt.vendor || '—'}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Fecha</div>
                <div style={{ fontSize: 14, color: NAVY }}>{receipt.date || '—'}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Método de Pago</div>
                <div style={{ fontSize: 14, color: NAVY }}>
                  {receipt.paymentMethod ? (PAYMENT_METHODS[receipt.paymentMethod] || receipt.paymentMethod) : '—'}
                  {receipt.cardType && (
                    <span style={{ fontSize: 12, color: '#64748b', marginLeft: 6 }}>
                      ({receipt.cardType === 'amex' ? 'American Express' : receipt.cardType === 'home_depot' ? 'Home Depot' : receipt.cardType === 'mastercard' ? 'Mastercard' : 'Visa'})
                    </span>
                  )}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Total</div>
                <div style={{ fontSize: 17, fontWeight: 700, color: NAVY }}>
                  {fmt(items.length > 0
                    ? items.reduce((s, i) => { const a = parseFloat(i.amount)||0; return s + a + a*(parseFloat(i.taxRate)||0)/100; }, 0)
                    : receipt.amount)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Feedback is now shown inline per project section below */}

      {/* Line items — grouped by project */}
      {items.length > 0 && (
        <div style={{ ...bubbleStyle, padding: 0, overflow: 'hidden' }}>
          <div style={{
            padding: '12px 18px', background: '#f8fafc',
            borderBottom: `1px solid ${BORDER_COLOR}`,
          }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: NAVY, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Items del Recibo
            </span>
            <span style={{ fontSize: 12, color: '#64748b', marginLeft: 8 }}>
              {items.length} artículo{items.length !== 1 ? 's' : ''}
            </span>
          </div>

          {projectGroups.map(([projectName, projectItems]) => {
            const supervisorName = projectItems[0]?.supervisorName || '—';
            const sectionTotal = projectItems.reduce((s, i) => {
              const amt = parseFloat(i.amount) || 0;
              return s + amt + amt * (parseFloat(i.taxRate) || 0) / 100;
            }, 0);
            const sectionFeedback = projectItems.find(i => i.reviewReason && (i.status === 'sent_back' || i.status === 'rejected'));
            return (
            <div key={projectName}>
              {/* Project sub-header */}
              <div style={{
                padding: '8px 18px', background: '#f0f4ff',
                fontWeight: 600, fontSize: 13, color: NAVY,
                borderBottom: `1px solid ${BORDER_COLOR}`,
              }}>
                {projectName}
                <span style={{ color: '#64748b', fontWeight: 400, marginLeft: 8 }}>Supervisor: {supervisorName}</span>
                <span style={{ float: 'right', fontWeight: 700 }}>{fmt(sectionTotal)}</span>
              </div>

              {/* Table header */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 45px 70px 55px 80px 80px 80px',
                background: '#f8fafc',
                fontWeight: '600', fontSize: 11, color: '#64748b',
                padding: '8px 18px', gap: 8,
                borderBottom: `1px solid ${BORDER_COLOR}`,
                letterSpacing: '0.03em',
              }}>
                <div>Descripción</div>
                <div style={{ textAlign: 'center' }}>Cant.</div>
                <div style={{ textAlign: 'right' }}>Monto</div>
                <div style={{ textAlign: 'right' }}>Tax%</div>
                <div style={{ textAlign: 'right' }}>Tax $</div>
                <div style={{ textAlign: 'right' }}>Total</div>
                <div style={{ textAlign: 'center' }}>Estado</div>
              </div>

              {/* Items */}
              {projectItems.map((item) => {
                const amt = parseFloat(item.amount) || 0;
                const taxAmt = amt * (parseFloat(item.taxRate) || 0) / 100;
                return (
                <div key={item.id} style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 45px 70px 55px 80px 80px 80px',
                  padding: '10px 18px', gap: 8,
                  borderBottom: '1px solid #f1f5f9',
                  alignItems: 'center', background: '#fff',
                }}>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: 13, color: '#1e293b' }}>
                      {item.description || '—'}
                    </div>
                    {item.isPersonal && (
                      <span style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 10, padding: '1px 6px', fontSize: 10, fontWeight: 600 }}>Personal</span>
                    )}
                  </div>
                  <div style={{ textAlign: 'center', fontSize: 12, color: '#475569', fontWeight: 600 }}>
                    {item.quantity || 1}
                  </div>
                  <div style={{ fontWeight: '700', fontSize: 13, color: '#1e293b', textAlign: 'right' }}>
                    {fmt(amt / (item.quantity || 1))}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', textAlign: 'right' }}>
                    {item.taxRate > 0 ? `${item.taxRate}%` : '—'}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', textAlign: 'right', fontWeight: 500 }}>
                    {taxAmt > 0 ? fmt(taxAmt) : '—'}
                  </div>
                  <div style={{ fontWeight: '700', fontSize: 13, color: '#1e293b', textAlign: 'right' }}>
                    {fmt(amt + taxAmt)}
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <ItemStatusBadge status={item.status} />
                  </div>
                </div>
                );
              })}

              {/* Inline supervisor feedback for this section */}
              {sectionFeedback && (
                <div style={{
                  padding: '8px 18px',
                  background: sectionFeedback.status === 'sent_back' ? '#fffbeb' : '#fef2f2',
                  borderBottom: `1px solid ${sectionFeedback.status === 'sent_back' ? '#fcd34d' : '#fecaca'}`,
                  fontSize: 12,
                }}>
                  <span style={{ fontWeight: 600, color: '#1e293b' }}>{sectionFeedback.reviewReason}</span>
                  {sectionFeedback.reviewNotes && (
                    <span style={{ color: '#64748b', fontStyle: 'italic', marginLeft: 8 }}>{sectionFeedback.reviewNotes}</span>
                  )}
                </div>
              )}
            </div>
            );
          })}

          {/* Grand total */}
          {(() => {
            const subtotal = items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
            const totalTax = items.reduce((s, i) => {
              const amt = parseFloat(i.amount) || 0;
              return s + amt * (parseFloat(i.taxRate) || 0) / 100;
            }, 0);
            const grandTotal = subtotal + totalTax;
            return (
              <div style={{ padding: '12px 18px', background: '#f8fafc', display: 'flex', justifyContent: 'flex-end', gap: 24, alignItems: 'center' }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginBottom: 2 }}>Subtotal</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>{fmt(subtotal)}</div>
                </div>
                {totalTax > 0 && (
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginBottom: 2 }}>Tax</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>{fmt(totalTax)}</div>
                  </div>
                )}
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginBottom: 2 }}>Total General</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: NAVY }}>{fmt(grandTotal)}</div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Receipt viewer modal */}
      {showReceiptModal && previewSrc && (
        <ReceiptViewerModal
          receipt={{
            vendor: receipt.vendor || 'Recibo',
            status: receipt.status,
            amount: receipt.amount,
            date: receipt.date,
            fileName: receipt.fileName,
            fileType: receipt.fileType,
            previewUrl: previewSrc,
          }}
          hasPrev={false}
          hasNext={false}
          onPrev={() => {}}
          onNext={() => {}}
          onClose={() => setShowReceiptModal(false)}
        />
      )}
    </div>
  );
}
